import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import useApi from "../hooks/useApi";
import { Link } from "react-router-dom";
import { 
  HiCreditCard, HiCheckCircle, HiExclamationCircle, HiCurrencyDollar, 
  HiCalendar, HiClock, HiArrowUp, HiArrowRight, HiCpuChip 
} from "react-icons/hi2";

const SubscriptionManagement = () => {
  const api = useApi();
  const { user, token } = useAuth();
  
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCards, setIsLoadingCards] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [selectedNewPlan, setSelectedNewPlan] = useState("");
  const [isProcessingUpgrade, setIsProcessingUpgrade] = useState(false);
  const [creditsToBuy, setCreditsToBuy] = useState(1);

  const planPrices = {
    trail: "Free (30 days)",
    starter_monthly: "$99/month",
    pro_monthly: "$199/month",
    franchise_monthly: "$399/month",
  };

  const planOrder = ["trail", "starter_monthly", "pro_monthly", "franchise_monthly"];

  // Backend API Endpoints (relative to BASE_URL in useApi.js)
  const backendGetSubscriptionApiUrl = "subscription/current";
  const backendGetPlansApiUrl = "subscription/plans";
  const backendCreateStripeSessionApiUrl = "payment/create-stripe-session";
  const backendGetStripePaymentMethodsApiUrl = "payment/stripe-payment-methods";
  const backendBuyCreditsApiUrl = "credits/buy";
  const backendVerifyCreditPurchasePaymentApiUrl = "credits/verify-payment";
  const backendUpdateCardApiUrl = "payment/update-card";

  const [availablePlans, setAvailablePlans] = useState([]);

  useEffect(() => {
    if (user && token) {
      fetchCurrentSubscription();
      fetchAvailablePlans();
      fetchPaymentMethods();
    } else {
      setMessage("You must be logged in to manage subscriptions.");
      setMessageType("error");
      setIsLoading(false);
    }
  }, [user, token]);

  const fetchPaymentMethods = async () => {
    setIsLoadingCards(true);
    try {
      const result = await api.get(backendGetStripePaymentMethodsApiUrl);
      if (result.status === 200) {
        setPaymentMethods(result.data);
      }
    } catch (error) {
      console.error("Error fetching payment methods:", error);
    } finally {
      setIsLoadingCards(false);
    }
  };

  const fetchAvailablePlans = async () => {
    try {
      const result = await api.get(backendGetPlansApiUrl);
      if (result.status === 200) {
        setAvailablePlans(result.data);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const fetchCurrentSubscription = async () => {
    setIsLoading(true);
    setMessage("");
    setMessageType("");

    try {
      const result = await api.get(backendGetSubscriptionApiUrl);

      if (result.status === 200) {
        setCurrentSubscription(result.data);
        setMessageType("success");
      } else {
        setMessage(`Failed to load subscription: ${result.message || "Unknown error"}`);
        setMessageType("error");
      }
    } catch (error) {
      console.error("Error fetching current subscription:", error);
      setMessage(`Error loading subscription: ${error.message}.`);
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgradeButtonClick = () => {
    document.getElementById("upgradePlanFormSection")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleUpgradeRequest = async (event) => {
    event.preventDefault();
    setIsProcessingUpgrade(true);
    setMessage("");
    setMessageType("");

    if (!selectedNewPlan) {
      setMessage("Please select a plan to upgrade to.");
      setMessageType("error");
      setIsProcessingUpgrade(false);
      return;
    }

    const planToUpgrade = availablePlans.find(p => p.key === selectedNewPlan);
    if (!planToUpgrade) {
      setMessage("Plan details not found.");
      setMessageType("error");
      setIsProcessingUpgrade(false);
      return;
    }

    try {
      const result = await api.post(backendCreateStripeSessionApiUrl, {
        planId: planToUpgrade._id,
        successUrl: window.location.origin + "/subscription?status=success",
        cancelUrl: window.location.origin + "/subscription?status=cancel",
      });

      if (result.status === 200 && result.data.url) {
        window.location.href = result.data.url;
      } else {
        setMessage(`Failed to initiate upgrade: ${result.message || "Unknown error"}`);
        setMessageType("error");
      }
    } catch (error) {
      setMessage(`Error: ${error.message}`);
      setMessageType("error");
    } finally {
      setIsProcessingUpgrade(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (window.confirm("Are you sure you want to cancel? This relies on your backend implementation.")) {
      // Stub for cancellation
      alert("Please contact support to cancel for now.");
    }
  };

  // UI Helpers
  const getProgressColor = (remaining, total) => {
    if (total === 0) return "w-0";
    const percentage = (remaining / total) * 100;
    if (percentage > 50) return "bg-green-500";
    if (percentage > 20) return "bg-yellow-400";
    return "bg-red-500";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric", month: "short", day: "numeric",
    });
  };

  const formatCurrency = (amount, currency = "USD") => {
    if (isNaN(amount)) return "N/A";
    const formatter = new Intl.NumberFormat("en-IN", {
      style: "currency", currency: currency, maximumFractionDigits: 2,
    });
    return formatter.format(amount / 100);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-svinno-600"></div>
        <p className="mt-4 text-slate-500 font-medium animate-pulse">Loading subscription details...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto pb-12">
        {/* Helper Action Bar */}
        <div className="flex justify-between items-end mb-6">
            <div>
                <h2 className="text-2xl font-bold text-slate-900 font-display">Subscription</h2>
                <p className="text-slate-500 text-sm mt-1">Manage your plan, billing details, and limits.</p>
            </div>
            <Link
                to={`/subscription-transactions?subscriptionId=${currentSubscription?.stripeSubscriptionId || currentSubscription?.razorpaySubscriptionId}`}
                className="inline-flex items-center px-4 py-2 bg-white border border-slate-200 text-slate-700 font-semibold rounded-lg shadow-sm hover:bg-slate-50 hover:text-svinno-600 transition-all text-sm"
            >
                <HiClock className="w-5 h-5 mr-2 text-slate-400" />
                History
            </Link>
        </div>

        {/* Alerts */}
        {message && (
             <div className={`p-4 rounded-xl mb-6 flex items-start gap-3 border shadow-sm ${
                 messageType === "error" ? "bg-red-50 border-red-100 text-red-700" : 
                 messageType === "success" ? "bg-green-50 border-green-100 text-green-700" :
                 "bg-blue-50 border-blue-100 text-blue-700"
             }`}>
                 <HiExclamationCircle className="w-5 h-5 mt-0.5 shrink-0" />
                 <span className="font-medium text-sm">{message}</span>
             </div>
        )}

        {!currentSubscription ? (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-8 text-center border-b border-slate-100 bg-slate-50">
                    <div className="w-16 h-16 bg-svinno-100 text-svinno-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <HiCreditCard className="w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">Activate Your Subscription</h3>
                    <p className="text-slate-500 max-w-lg mx-auto">
                        You have not activated a subscription yet. Please select a plan below to unlock all features and start managing your store.
                    </p>
                </div>
                
                <div className="p-8">
                    <h4 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                        <HiArrowUp className="w-5 h-5 text-svinno-500" />
                        Choose a Plan
                    </h4>
                    
                    <form onSubmit={handleUpgradeRequest} className="max-w-2xl mx-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                            {planOrder.filter(p => p !== 'trail').map((plan) => {
                                if (!planPrices[plan]) return null;
                                const isSelected = selectedNewPlan === plan;
                                return (
                                    <div 
                                        key={plan}
                                        onClick={() => setSelectedNewPlan(plan)}
                                        className={`cursor-pointer rounded-2xl border-2 p-6 transition-all relative ${
                                            isSelected ? 'border-svinno-500 bg-svinno-50 shadow-md shadow-svinno-500/10' : 'border-slate-100 hover:border-slate-200 bg-white'
                                        }`}
                                    >
                                        {isSelected && (
                                            <div className="absolute top-3 right-3 text-svinno-600">
                                                <HiCheckCircle className="w-6 h-6" />
                                            </div>
                                        )}
                                        <p className="font-bold text-slate-900 capitalize text-lg mb-1">{plan.replace('_monthly', '')}</p>
                                        <p className="text-2xl font-black text-svinno-600 mb-4">{planPrices[plan]}<span className="text-xs text-slate-400 font-normal"> / month</span></p>
                                        <ul className="text-xs text-slate-500 space-y-2">
                                            <li className="flex items-center gap-2">
                                                <HiCheckCircle className="w-4 h-4 text-green-500" /> Full Access
                                            </li>
                                            <li className="flex items-center gap-2">
                                                <HiCheckCircle className="w-4 h-4 text-green-500" /> Support Included
                                            </li>
                                        </ul>
                                    </div>
                                );
                            })}
                        </div>
                        
                        <button
                            type="submit"
                            disabled={!selectedNewPlan || isProcessingUpgrade}
                            className="w-full flex justify-center items-center py-4 px-6 rounded-xl font-bold text-lg text-white bg-svinno-600 hover:bg-svinno-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl shadow-svinno-500/20 transition-all"
                        >
                            {isProcessingUpgrade ? (
                                <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            ) : (
                                <>Activate Plan Now <HiArrowRight className="ml-2 w-5 h-5" /></>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        ) : (
            <div className="space-y-6">
                
                {/* 1. Main Plan Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="p-6 sm:p-8 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                             <div className="flex items-center gap-3 mb-1">
                                <h3 className="text-3xl font-bold text-slate-900 capitalize font-display">
                                    {currentSubscription.currentPlan || "N/A"}
                                </h3>
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide border ${
                                    currentSubscription.subscriptionStatus === 'active' 
                                    ? 'bg-green-50 text-green-700 border-green-200' 
                                    : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                                }`}>
                                    {currentSubscription.subscriptionStatus}
                                </span>
                             </div>
                             <p className="text-slate-500 text-sm">
                                Renews on <span className="font-semibold text-slate-700">{formatDate(currentSubscription.renewOn)}</span>
                             </p>
                        </div>

                        <div className="flex gap-3">
                            {currentSubscription.currentPlan !== 'franchise_monthly' && (
                                <button 
                                    onClick={handleUpgradeButtonClick}
                                    className="px-5 py-2.5 bg-svinno-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-svinno-500/30 hover:bg-svinno-700 hover:-translate-y-0.5 transition-all flex items-center gap-2"
                                >
                                    <HiArrowUp className="w-4 h-4" />
                                    Upgrade Plan
                                </button>
                            )}
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
                        <div className="p-6">
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Billing Amount</p>
                            <div className="text-xl font-bold text-slate-800 flex items-center gap-1">
                                {formatCurrency(currentSubscription.nextBillingAmount, currentSubscription.nextBillingCurrency)}
                                <span className="text-sm text-slate-400 font-normal">/ {currentSubscription.nextBillingFrequency?.replace('/', '')}</span>
                            </div>
                        </div>
                        <div className="p-6">
                             <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Payment Method</p>
                             <div className="flex items-center gap-2">
                                <HiCreditCard className="w-5 h-5 text-slate-400" />
                                <span className="text-slate-700 font-medium">
                                    {currentSubscription.paymentMethodType || "Card"} 
                                    {currentSubscription.paymentMethodLast4 ? ` •••• ${currentSubscription.paymentMethodLast4}` : ''}
                                </span>
                             </div>
                        </div>
                        <div className="p-6">
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Start Date</p>
                            <p className="text-slate-700 font-medium">{formatDate(currentSubscription.startDate)}</p>
                        </div>
                        <div className="p-6">
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Valid Until</p>
                            <p className="text-slate-700 font-medium">{formatDate(currentSubscription.validUntil)}</p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* 2. Usage Stats */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
                        <div className="flex justify-between items-center mb-6">
                            <h4 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                <HiCpuChip className="w-5 h-5 text-svinno-500" />
                                Plan Usage
                            </h4>
                            <button onClick={fetchCurrentSubscription} className="text-xs font-semibold text-svinno-600 hover:text-svinno-700">Refesh Data</button>
                        </div>
                        
                        <div className="mb-6">
                            <div className="flex justify-between text-sm mb-2">
                                <span className="text-slate-600 font-medium">Credits Used</span>
                                <span className="text-slate-900 font-bold">{currentSubscription.remainingCredits} / {currentSubscription.totalCredits}</span>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                                <div 
                                    className={`h-full rounded-full transition-all duration-500 ${getProgressColor(currentSubscription.remainingCredits, currentSubscription.totalCredits)}`}
                                    style={{ width: `${(currentSubscription.remainingCredits / currentSubscription.totalCredits) * 100}%` }}
                                ></div>
                            </div>
                            <p className="text-xs text-slate-400 mt-2">Credits reset on next billing cycle.</p>
                        </div>
                    </div>

                    {/* 3. Upgrade Form */}
                    <div id="upgradePlanFormSection" className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
                        <h4 className="text-lg font-bold text-slate-900 mb-4">Change Plan</h4>
                        <form onSubmit={handleUpgradeRequest}>
                            <div className="space-y-4">
                                <label className="block text-sm font-medium text-slate-700 sr-only">Select Plan</label>
                                <div className="space-y-3">
                                    {planOrder.map((plan) => {
                                        if (plan === 'trail' || plan === currentSubscription.currentPlan) return null;
                                        if (!planPrices[plan]) return null;
                                        
                                        const isSelected = selectedNewPlan === plan;
                                        
                                        return (
                                            <div 
                                                key={plan}
                                                onClick={() => setSelectedNewPlan(plan)}
                                                className={`cursor-pointer rounded-xl border p-4 flex justify-between items-center transition-all ${
                                                    isSelected ? 'border-svinno-500 bg-svinno-50 ring-1 ring-svinno-500' : 'border-slate-200 hover:border-slate-300'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${isSelected ? 'border-svinno-600 bg-svinno-600' : 'border-slate-300'}`}>
                                                        {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white"></div>}
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-slate-800 capitalize">{plan}</p>
                                                    </div>
                                                </div>
                                                <p className="font-medium text-slate-600">{planPrices[plan]}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                                
                                <button
                                    type="submit"
                                    disabled={!selectedNewPlan || isProcessingUpgrade}
                                    className="w-full mt-4 flex justify-center items-center py-3 px-4 rounded-xl font-bold text-white bg-svinno-600 hover:bg-svinno-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-all"
                                >
                                    {isProcessingUpgrade ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    ) : (
                                        <>Proceed to Payment <HiArrowRight className="ml-2 w-4 h-4" /></>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* 4. Saved Payment Methods */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
                    <h4 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                        <HiCreditCard className="w-5 h-5 text-svinno-500" />
                        Saved Payment Methods
                    </h4>
                    
                    {isLoadingCards ? (
                        <div className="flex justify-center py-8">
                            <div className="w-8 h-8 border-4 border-svinno-100 border-t-svinno-600 rounded-full animate-spin"></div>
                        </div>
                    ) : paymentMethods.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {paymentMethods.map((method) => (
                                <div key={method.id} className="border border-slate-200 rounded-xl p-4 flex items-center justify-between hover:border-svinno-200 transition-colors">
                                    <div className="flex items-center gap-4">
                                        <div className="bg-slate-50 p-2 rounded-lg">
                                            <HiCreditCard className="w-6 h-6 text-slate-400" />
                                        </div>
                                        <div>
                                            <p className="font-bold text-slate-800 capitalize">{method.brand} •••• {method.last4}</p>
                                            <p className="text-xs text-slate-500">Expires {method.expMonth}/{method.expYear}</p>
                                        </div>
                                    </div>
                                    {currentSubscription.paymentMethodLast4 === method.last4 && (
                                        <span className="text-[10px] font-bold bg-green-50 text-green-700 px-2 py-0.5 rounded-full uppercase border border-green-200">Default</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                            <p className="text-slate-500 text-sm">No saved cards found. They will appear here after your first payment.</p>
                        </div>
                    )}
                </div>
            </div>
        )}
    </div>
  );
};

export default SubscriptionManagement;
